/**
 * NetworkHealthMonitor — derives a network profile from connection,
 * latency, polling failures, and FPS. Pure JS, no backend.
 *
 * Profiles: excellent | good | degraded | critical
 */
import { useSyncExternalStore } from "react";
import { getProfile as getMmm } from "@/lib/launchHardening/mobileMatchMode";
import { breakerSnapshot } from "./circuitBreaker";

export type NetworkProfile = "excellent" | "good" | "degraded" | "critical";

export interface NetworkHealth {
  profile: NetworkProfile;
  online: boolean;
  effectiveType: string;
  downlink: number;       // Mbps
  rtt: number;            // ms
  saveData: boolean;
  avgLatencyMs: number;   // rolling avg of query latencies
  failRate: number;       // 0..1 polling failure ratio
  fps: number;
  reason: string;
}

type ConnLike = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  addEventListener?: (t: string, fn: () => void) => void;
};

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

const latencies: number[] = [];
const MAX_LAT = 20;
let recentOk = 0;
let recentFail = 0;
const FAIL_WINDOW = 30;

function rollFail() {
  const total = recentOk + recentFail;
  if (total > FAIL_WINDOW) {
    const ratio = recentFail / total;
    recentOk = Math.round((1 - ratio) * (FAIL_WINDOW * 0.5));
    recentFail = Math.round(ratio * (FAIL_WINDOW * 0.5));
  }
}

export function recordQueryLatency(ms: number) {
  if (!Number.isFinite(ms) || ms < 0) return;
  latencies.push(ms);
  if (latencies.length > MAX_LAT) latencies.shift();
  recentOk += 1; rollFail();
}

export function recordQueryFailure() {
  recentFail += 1; rollFail();
}

function avg(arr: number[]) {
  if (!arr.length) return 0;
  let s = 0; for (const v of arr) s += v;
  return s / arr.length;
}

function classify(h: Omit<NetworkHealth, "profile" | "reason">): { profile: NetworkProfile; reason: string } {
  if (!h.online) return { profile: "critical", reason: "offline" };
  if (h.saveData) return { profile: "degraded", reason: "save-data" };
  if (h.effectiveType === "slow-2g" || h.effectiveType === "2g") return { profile: "critical", reason: "2g" };
  if (h.failRate > 0.4) return { profile: "critical", reason: "queries failing" };
  if (h.avgLatencyMs > 2200 || h.rtt > 1200) return { profile: "critical", reason: "high latency" };
  if (h.effectiveType === "3g" || h.downlink && h.downlink < 1.2) return { profile: "degraded", reason: "slow link" };
  if (h.failRate > 0.15 || h.avgLatencyMs > 1200 || h.fps < 35) return { profile: "degraded", reason: "unstable" };
  if (h.fps < 50 || h.avgLatencyMs > 600 || (h.downlink && h.downlink < 3)) return { profile: "good", reason: "stable" };
  return { profile: "excellent", reason: "optimal" };
}

function readConn(): ConnLike | undefined {
  if (typeof navigator === "undefined") return undefined;
  const n = navigator as Navigator & { connection?: ConnLike; mozConnection?: ConnLike; webkitConnection?: ConnLike };
  return n.connection || n.mozConnection || n.webkitConnection;
}

function compute(): NetworkHealth {
  const conn = readConn();
  const mmm = getMmm();
  const failRate = (recentOk + recentFail) > 0 ? recentFail / (recentOk + recentFail) : 0;
  // Pull breaker pressure as a soft failure signal too.
  const breakerOpens = breakerSnapshot().filter((b) => b.state !== "closed").length;
  const adjFail = Math.min(1, failRate + breakerOpens * 0.1);
  const base = {
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    effectiveType: conn?.effectiveType ?? "4g",
    downlink: conn?.downlink ?? 10,
    rtt: conn?.rtt ?? 0,
    saveData: !!conn?.saveData,
    avgLatencyMs: Math.round(avg(latencies)),
    failRate: adjFail,
    fps: mmm.fps,
  };
  const { profile, reason } = classify(base);
  return { ...base, profile, reason };
}

let cached: NetworkHealth = compute();

function refresh() {
  const next = compute();
  if (
    next.profile !== cached.profile ||
    next.online !== cached.online ||
    next.effectiveType !== cached.effectiveType ||
    Math.abs(next.fps - cached.fps) > 4 ||
    Math.abs(next.avgLatencyMs - cached.avgLatencyMs) > 150
  ) {
    cached = next;
    emit();
  } else {
    cached = next;
  }
}

let started = false;
export function initNetworkHealth() {
  if (started || typeof window === "undefined") return;
  started = true;
  const conn = readConn();
  conn?.addEventListener?.("change", refresh);
  window.addEventListener("online", refresh);
  window.addEventListener("offline", refresh);
  setInterval(refresh, 4_000);
}

export function getNetworkHealth(): NetworkHealth {
  return cached;
}

export function useNetworkHealth(): NetworkHealth {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => cached,
    () => cached,
  );
}

/** Adaptive multipliers, derived from network profile. */
export function adaptiveMultipliers(p: NetworkProfile = cached.profile) {
  switch (p) {
    case "critical": return { density: 0.25, glow: 0.3, overlays: 1, animation: 0.4, pollMs: 60_000 };
    case "degraded": return { density: 0.5, glow: 0.55, overlays: 1, animation: 0.65, pollMs: 30_000 };
    case "good":     return { density: 0.8, glow: 0.85, overlays: 2, animation: 0.9,  pollMs: 18_000 };
    case "excellent":
    default:         return { density: 1,   glow: 1,    overlays: 2, animation: 1,    pollMs: 12_000 };
  }
}

/** Adaptive react-query refetchInterval helper. */
export function adaptivePollMs(base = 15_000): number {
  const m = adaptiveMultipliers();
  return Math.max(base, m.pollMs);
}

/** Wrap any async fetcher to feed latency/failure metrics automatically. */
export async function withNetworkProbe<T>(fn: () => Promise<T>): Promise<T> {
  const t0 = (typeof performance !== "undefined" ? performance.now() : Date.now());
  try {
    const v = await fn();
    recordQueryLatency((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0);
    return v;
  } catch (e) {
    recordQueryFailure();
    throw e;
  }
}
